// src/admin/hooks/useAdmin.ts — FINAL COMPLETE & MERGED VERSION
// All older functionality retained + RTP ready + real-time sync + no 400 errors

import { useState, useEffect, useCallback, useRef } from 'react';
import io, { Socket } from 'socket.io-client';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchCsrfToken } from '../../shared/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
export { API_BASE_URL as BASE_URL };

const RTP_MAX_DECIMAL = 1.5;

let hasLoggedToken = false;
let sharedAdminSocket: Socket | null = null;

const clearAdminSession = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminInfo');
  hasLoggedToken = false;
};

// === INTERFACES (full from older code) ===
interface User {
  id: string;
  login_id?: string;
  email?: string;
  name: string;
  phone?: string;
  manager_email?: string;
  real_credits: number;
  mode: 'cash';
  real_money_approved: boolean;
  banned: boolean;
  suspended: boolean;
}

interface Manager {
  id: string;
  login_id?: string;
  name: string;
  email?: string;
  phone?: string;
  credits: number;
  cashout_rate?: number;
  is_verified: boolean;
  suspended: boolean;
}

interface Admin {
  id?: string;
  login_id?: string;
  email?: string;
  name: string;
  phone?: string;
  is_verified: boolean;
  banned: boolean;
  is_super_admin?: boolean;
}

interface Table {
  id: string;
  name: string;
  location?: string;
  manager_email?: string;
  current_rtp: number;
  payout_ratio: number;
  created_at: string;
  jackpot?: number;
  playerCount?: number;
}

interface VerificationRequest {
  id: string;
  user_id: string;
  email: string;
  request_date: string;
}

interface Cashout {
  id: string;
  user_id: string;
  user_email: string;
  manager_email?: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export default function useAdmin() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('adminToken'));
  const [error, setError] = useState<string>('');
  const [socket, setSocket] = useState<Socket | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const initialized = useRef(false);
  const socketRef = useRef<Socket | null>(null);

  const authPages = ['/admin/login', '/admin/signup', '/admin/forgot-password', '/admin/reset-password'];
  const isAuthPage = authPages.includes(location.pathname);

  // === TOKEN HANDLING ===
  const getTokenWithRetry = useCallback(async (maxAttempts = 3): Promise<string | null> => {
    for (let i = 0; i < maxAttempts; i++) {
      const t = localStorage.getItem('adminToken');
      if (t) {
        if (!hasLoggedToken) {
          console.log('Token retrieved:', t.substring(0, 20) + '...');
          hasLoggedToken = true;
        }
        return t;
      }
      await new Promise(r => setTimeout(r, 100));
    }
    return null;
  }, []);

  // === SOCKET.IO CONNECTION — FIXED & ROBUST ===
  const initializeSocket = useCallback(async () => {
    if (socketRef.current) return;

    if (sharedAdminSocket) {
      socketRef.current = sharedAdminSocket;
      setSocket(sharedAdminSocket);
      return;
    }

    const t = await getTokenWithRetry();
    if (!t) {
      setError('Authentication required');
      navigate('/admin/login');
      return;
    }

    const s = io(API_BASE_URL, {
      path: '/socket.io',
      transports: ['polling'],
      upgrade: false,
      auth: { token: t },
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
    });

    s.on('connect', () => {
      console.log('Admin Socket.IO CONNECTED SUCCESSFULLY');
      s.emit('subscribe-rtp'); // Optional: join RTP room
    });

    s.on('connect_error', (err) => {
      console.error('Socket connection failed:', err.message);
      if (err.message.includes('authentication') || err.message.includes('token')) {
        clearAdminSession();
        s.removeAllListeners();
        s.disconnect();
        if (sharedAdminSocket === s) sharedAdminSocket = null;
        if (socketRef.current === s) socketRef.current = null;
        setSocket(null);
        setToken(null);
        navigate('/admin/login', { replace: true });
      }
    });

    s.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      // Do NOT clear token on transient disconnects (minimize/sleep/network).
      // Token should be cleared only on explicit auth failure (handled in connect_error).
    });

    socketRef.current = s;
    sharedAdminSocket = s;
    setSocket(s);
  }, [navigate, getTokenWithRetry]);

  // === TOKEN VALIDATION ===
  const validateToken = useCallback(async () => {
    if (isAuthPage) return false;
    const t = token || await getTokenWithRetry();
    if (!t) {
      navigate('/admin/login');
      return false;
    }

    try {
      const csrfToken = await fetchCsrfToken();
      const res = await fetch(`${API_BASE_URL}/admin/validate`, {
        headers: {
          Authorization: `Bearer ${t}`,
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
      });

      if (!res.ok) throw new Error('Invalid session');
      const payload = await res.json();
      if (payload.admin) {
        localStorage.setItem('adminInfo', JSON.stringify(payload.admin));
      }
      setToken(t);
      await initializeSocket();
      return true;
    } catch {
      clearAdminSession();
      socketRef.current?.removeAllListeners();
      socketRef.current?.disconnect();
      socketRef.current = null;
      sharedAdminSocket = null;
      setSocket(null);
      setToken(null);
      navigate('/admin/login', { replace: true });
      return false;
    }
  }, [isAuthPage, token, navigate, initializeSocket, getTokenWithRetry]);

  // === CSRF REFRESH ===
  const refreshCsrf = useCallback(async () => {
    try {
      const csrf = await fetchCsrfToken();
      document.querySelectorAll('[name="_csrf"]').forEach(el => {
        (el as HTMLInputElement).value = csrf;
      });
      return csrf;
    } catch {
      return null;
    }
  }, []);

  // === AUTH ACTIONS ===
  const forgotPassword = useCallback(async (email: string) => {
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ email, _csrf: csrf }),
    });
    if (!res.ok) throw new Error('Failed to send reset link');
    alert('Password reset link sent');
  }, [refreshCsrf]);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ token, newPassword, _csrf: csrf }),
    });
    if (!res.ok) throw new Error('Failed to reset password');
    alert('Password reset successful');
    navigate('/admin/login');
  }, [refreshCsrf, navigate]);

  const logout = useCallback(() => {
    clearAdminSession();
    setError('');
    setToken(null);
    setSocket(null);
    socketRef.current?.removeAllListeners();
    socketRef.current?.disconnect();
    socketRef.current = null;
    sharedAdminSocket?.removeAllListeners();
    sharedAdminSocket?.disconnect();
    sharedAdminSocket = null;
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  // === DATA FETCHERS ===
  const fetchUsers = useCallback(async (): Promise<User[]> => {
    const t = await getTokenWithRetry();
    if (!t) return [];
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/users`, {
      headers: { Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to fetch users');
    return res.json();
  }, [getTokenWithRetry, refreshCsrf]);

  const fetchAdmins = useCallback(async (): Promise<Admin[]> => {
    const t = await getTokenWithRetry();
    if (!t) return [];
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/admins`, {
      headers: { Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to fetch admins');
    return res.json();
  }, [getTokenWithRetry, refreshCsrf]);

  const fetchManagers = useCallback(async (): Promise<Manager[]> => {
    const t = await getTokenWithRetry();
    if (!t) return [];
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/managers`, {
      headers: { Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to fetch managers');
    return res.json();
  }, [getTokenWithRetry, refreshCsrf]);

  const fetchTables = useCallback(async (): Promise<Table[]> => {
    const t = await getTokenWithRetry();
    if (!t) return [];
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/tables`, {
      headers: { Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to fetch tables');
    const data = await res.json();
    return (data || []).map((table: any) => {
      const rawRtp = Number(table.current_rtp ?? 0);
      return {
        ...table,
        current_rtp: rawRtp > RTP_MAX_DECIMAL ? rawRtp / 100 : rawRtp,
        payout_ratio: Number(table.payout_ratio ?? 0),
        jackpot: Number(table.jackpot ?? 0),
      };
    });
  }, [getTokenWithRetry, refreshCsrf]);

  const fetchVerificationRequests = useCallback(async (): Promise<VerificationRequest[]> => {
    const t = await getTokenWithRetry();
    if (!t) return [];
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/verification-requests`, {
      headers: { Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to fetch requests');
    return res.json();
  }, [getTokenWithRetry, refreshCsrf]);

  const fetchCashouts = useCallback(async (): Promise<Cashout[]> => {
    const t = await getTokenWithRetry();
    if (!t) return [];
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/cashouts`, {
      headers: { Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to fetch cashouts');
    return res.json();
  }, [getTokenWithRetry, refreshCsrf]);

  // === ACTIONS ===
  const banUser = useCallback(async (user_id: string, banned: boolean) => {
    const t = await getTokenWithRetry();
    if (!t) return;
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/users/ban`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ user_id, banned, _csrf: csrf }),
    });
    if (!res.ok) throw new Error(`Failed to ${banned ? 'ban' : 'unban'} user`);
    alert(`User ${banned ? 'banned' : 'unbanned'}`);
  }, [getTokenWithRetry, refreshCsrf]);

  const suspendUser = useCallback(async (user_id: string, suspended: boolean) => {
    const t = await getTokenWithRetry();
    if (!t) return;
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/users/suspend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ user_id, suspended, _csrf: csrf }),
    });
    if (!res.ok) throw new Error(`Failed to ${suspended ? 'suspend' : 'unsuspend'} user`);
    alert(`User ${suspended ? 'suspended' : 'unsuspended'}`);
  }, [getTokenWithRetry, refreshCsrf]);

  const deleteUser = useCallback(async (user_id: string) => {
    const t = await getTokenWithRetry();
    if (!t) return;
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/users/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ user_id, _csrf: csrf }),
    });
    if (!res.ok) throw new Error('Failed to delete user');
    alert('User deleted');
  }, [getTokenWithRetry, refreshCsrf]);

  const suspendManager = useCallback(async (identifier: string, suspend: boolean) => {
    const t = await getTokenWithRetry();
    if (!t) return;
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/suspend-manager`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ email: identifier, suspend, _csrf: csrf }),
    });
    if (!res.ok) throw new Error(`Failed to ${suspend ? 'suspend' : 'unsuspend'} manager`);
  }, [getTokenWithRetry, refreshCsrf]);

  const deleteManager = useCallback(async (identifier: string) => {
    const t = await getTokenWithRetry();
    if (!t) return;
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/delete-manager`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ email: identifier, _csrf: csrf }),
    });
    if (!res.ok) throw new Error('Failed to delete manager');
  }, [getTokenWithRetry, refreshCsrf]);

  const deleteAdmin = useCallback(async (identifier: string) => {
    const t = await getTokenWithRetry();
    if (!t) return;
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/delete-admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ email: identifier, _csrf: csrf }),
    });
    if (!res.ok) throw new Error('Failed to delete admin');
  }, [getTokenWithRetry, refreshCsrf]);

  const verifyUser = useCallback(async (userId: string, approve: boolean) => {
    const t = await getTokenWithRetry();
    if (!t) return;
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/verify-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ userId, approve, _csrf: csrf }),
    });
    if (!res.ok) throw new Error(`Failed to ${approve ? 'approve' : 'reject'} user`);
    alert(`User ${approve ? 'approved' : 'rejected'}`);
  }, [getTokenWithRetry, refreshCsrf]);

  const handleCashout = useCallback(async (cashoutId: string, action: 'approve' | 'reject') => {
    const t = await getTokenWithRetry();
    if (!t) return;
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/cashout-${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ cashoutId, _csrf: csrf }),
    });
    if (!res.ok) throw new Error(`Failed to ${action} cashout`);
    alert(`Cashout ${action}d`);
  }, [getTokenWithRetry, refreshCsrf]);

  const deleteTable = useCallback(async (tableId: string) => {
    const t = await getTokenWithRetry();
    if (!t) return;
    const csrf = await refreshCsrf();
    const res = await fetch(`${API_BASE_URL}/admin/delete-table`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-CSRF-Token': csrf || '' },
      credentials: 'include',
      body: JSON.stringify({ tableId, _csrf: csrf }),
    });
    if (!res.ok) throw new Error('Failed to delete table');
    alert('Table deleted successfully');
  }, [getTokenWithRetry, refreshCsrf]);

  // === RTP & JACKPOT ACTIONS ===
  const changeTableRtp = useCallback(async (tableId: string, newRtp: number) => {
    if (!socket) throw new Error('Socket not connected');
    socket.emit('admin_change_rtp', { tableId, rtp: newRtp });
  }, [socket]);

  const forceJackpot = useCallback(async (tableId: string, amount: number = 50000) => {
    if (!socket) throw new Error('Socket not connected');
    socket.emit('admin_force_jackpot', { tableId, amount });
  }, [socket]);

  // === EFFECTS ===
  useEffect(() => {
    if (!isAuthPage && !initialized.current) {
      initialized.current = true;
      validateToken();
    }
  }, [validateToken, isAuthPage]);

  // === SOCKET EVENT HANDLERS ===
  useEffect(() => {
    if (!socket) return;

    // Game/RTP events
    socket.on('room_info', (data: { roomId: string; rtp: number; jackpot: number; playerCount: number }) => {
      console.log('ROOM_INFO received:', data);
      fetchTables();
    });

    socket.on('rtp-updated', (data: { tableId: string; rtp: number }) => {
      console.log('RTP UPDATED:', data);
      fetchTables();
    });

    socket.on('jackpot-win', (data: { winner: string; amount: number }) => {
      console.log('JACKPOT WIN:', data);
      fetchTables();
      fetchCashouts();
    });

    // General refresh events
    const refreshEvents = [
      'credits-updated',
      'cashout-updated',
      'verification-updated',
      'user-banned',
      'user-unbanned',
      'user-suspended',
      'user-unsuspended',
      'user-created',
      'manager-created',
      'table-created',
      'table-updated'
    ];

    refreshEvents.forEach(event => {
      socket.on(event, () => {
        fetchUsers();
        fetchManagers();
        fetchCashouts();
        fetchVerificationRequests();
        fetchTables();
      });
    });

    return () => {
      socket.off('room_info');
      socket.off('rtp-updated');
      socket.off('jackpot-win');
      refreshEvents.forEach(e => socket.off(e));
    };
  }, [socket, fetchTables, fetchUsers, fetchManagers, fetchCashouts, fetchVerificationRequests]);

  return {
    token,
    setToken,
    validateToken,
    refreshCsrf,
    forgotPassword,
    resetPassword,
    logout,
    error,
    setError,
    socket,
    fetchUsers,
    fetchAdmins,
    fetchManagers,
    fetchTables,
    fetchVerificationRequests,
    fetchCashouts,
    banUser,
    suspendUser,
    deleteUser,
    suspendManager,
    deleteManager,
    deleteAdmin,
    verifyUser,
    handleCashout,
    deleteTable,
    changeTableRtp,
    forceJackpot,
  };
}
